import { ArrowLeftIcon } from "@heroicons/react/outline";
import { CheckIcon, MinusIcon } from "@heroicons/react/solid";
import { find, flatten, head, isEmpty, keys, map, values } from "lodash";
import React, { Fragment, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import ExternalLink from "../../components/ExternalLink";
import { useBillingPlans } from "../../context/billingPlans";
import classNames from "../../utils/classNames";
import { PlanFeaturesMap, PlanType } from "../../utils/types";

interface Props {}

const CompareFeaturePlansPage: React.FC<Props> = () => {
  const { plans, setNewPlan, newPlan } = useBillingPlans();
  const history = useHistory();
  const [hideCommonFeatures, setHideCommonFeatures] = useState(false);

  const toggleHideCommonFeatures = () => setHideCommonFeatures((prev) => !prev);

  const businessPlanDetailedFeatures = useMemo(
    () => find(plans, { type: PlanType.business })?.detailed_features,
    [plans]
  );

  const commonFeatures = useMemo(() => {
    const featuresPerPlan = plans.map((plan) =>
      flatten(
        values(plan.detailed_features).map((featuresMap) => values(featuresMap))
      )
    );

    if (isEmpty(featuresPerPlan)) return [];

    const featuresCount = head(featuresPerPlan)?.length ?? 0;

    if (featuresPerPlan.some((features) => features.length !== featuresCount)) {
      return [];
    }

    const commonFeaturesList: string[] = [];

    for (let i = 0; i < featuresCount - 1; i++) {
      const feature = featuresPerPlan[0][i].title;
      const isCommon = featuresPerPlan.every((features) => !features[i].hidden);
      if (isCommon) commonFeaturesList.push(feature);
    }

    return commonFeaturesList;
  }, [plans]);

  const renderPlanSelection = useMemo(() => {
    if (isEmpty(plans)) {
      return (
        <span className="flex w-full h-10 rounded bg-gray-300 dark:bg-gray-600 animate-pulse" />
      );
    }

    return map(plans, (plan) => (
      <span
        key={plan.name}
        onClick={() => setNewPlan((prevState) => ({ ...prevState, plan }))}
        className={classNames(
          "text-sm w-full lg:py-0 py-3 lg:w-auto lg:justify-center dark:text-gray-200 font-semibold lg:border-r border-b lg:last:border-b last:border-b-0 lg:border-t pl-2 lg:px-5 h-full cursor-pointer flex items-center border-gray-300 dark:border-gray-500 lg:first:border-l last:border-l-0 lg:last:rounded-r-lg lg:first:rounded-l-lg",
          newPlan.plan?.type === plan.type
            ? "bg-hotspotty-400 dark:bg-hotspotty-400 border-hotspotty-400 text-white dark:text-gray-200"
            : "bg-white dark:bg-gray-800"
        )}
      >
        {plan.name}
      </span>
    ));
  }, [newPlan.plan?.type, plans, setNewPlan]);

  const renderHideCommonPlansSwitch = useMemo(
    () => (
      <div className="flex items-center mb-1">
        <button
          type="button"
          className={`ml-2 relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none ${
            hideCommonFeatures
              ? "bg-hotspotty-400 dark:bg-gray-100 dark:hover:bg-gray-200"
              : "bg-gray-200 dark:bg-gray-500 dark:hover:bg-gray-400"
          }`}
          aria-pressed="false"
          aria-labelledby="toggle-is-active"
          onClick={toggleHideCommonFeatures}
        >
          <span
            aria-hidden="true"
            className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200 ${
              hideCommonFeatures ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
        <div className="font-normal ml-2">Hide common features</div>
      </div>
    ),
    [hideCommonFeatures]
  );

  return (
    <div className="flex flex-col w-full items-center overflow-y-auto overflow-x-hidden pb-10 z-10">
      <div className="flex flex-col w-full max-w-7xl mx-auto px-4">
        {/* xs to lg */}
        <div className="max-w-2xl mx-auto lg:hidden flex flex-col w-full">
          <Link
            to="/pricing"
            className="flex mr-auto items-center mt-6 mb-6 cursor-pointer"
          >
            <ArrowLeftIcon className="w-3 mr-2" />
            <span className="text-sm font-bold">Return to pricing</span>
          </Link>

          <div className="flex-1 flex flex-col mb-8">
            <span className="dark:text-gray-200 mb-2">
              Select a feature plan to see what's included:
            </span>
            <div className="flex flex-col lg:flex-row items-center rounded-md lg:h-10 lg:mb-0 dark:text-gray-800 lg:border-0 border overflow-hidden dark:border-gray-500">
              {renderPlanSelection}
            </div>
          </div>

          {!isEmpty(commonFeatures) && (
            <div className="mb-5">{renderHideCommonPlansSwitch}</div>
          )}

          <section>
            {newPlan.plan?.detailed_features &&
              keys(newPlan.plan?.detailed_features).map((key) => (
                <table key={key} className="w-full">
                  <caption className="bg-gray-200 dark:bg-gray-700 border-t border-gray-200  dark:border-gray-500 py-3 px-4 text-sm font-medium text-gray-900 dark:text-white text-left capitalize">
                    {key}
                  </caption>
                  <thead>
                    <tr>
                      <th className="sr-only" scope="col">
                        Feature
                      </th>
                      <th className="sr-only" scope="col">
                        Included
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-500">
                    {values(
                      (newPlan.plan?.detailed_features as PlanFeaturesMap)[key]
                    ).map((feature, featureIndex) => {
                      if (
                        hideCommonFeatures &&
                        commonFeatures.includes(feature.title)
                      ) {
                        return null;
                      }

                      return (
                        <tr
                          key={`${feature.title}-${featureIndex}`}
                          className="flex justify-between w-full border-t border-gray-200 dark:border-gray-500"
                        >
                          <th
                            className="w-8/12 py-5 px-4 text-sm font-normal text-gray-500 dark:text-gray-300 text-left"
                            scope="row"
                          >
                            {feature.url ? (
                              <ExternalLink
                                className="break-normal overflow-visible whitespace-normal"
                                url={feature.url}
                                text={feature.title}
                              />
                            ) : (
                              feature.title
                            )}
                          </th>
                          <td className="w-4/12 py-5 pr-4">
                            {feature.hidden ? (
                              <MinusIcon
                                className="ml-auto h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                            ) : (
                              <CheckIcon
                                className="ml-auto h-5 w-5 text-green-500"
                                aria-hidden="true"
                              />
                            )}

                            <span className="sr-only">
                              {feature.hidden ? "No" : "Yes"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ))}

            <div className="py-5 border-b border-t border-gray-200 dark:border-gray-500 px-4">
              <button
                onClick={() => {
                  history.push("/pricing");
                }}
                className="w-full text-xs text-white py-2 mt-4 shadow rounded-md outline-none transition-all bg-hotspotty-400 border-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none"
              >
                Select {newPlan.plan?.name}
              </button>
            </div>
          </section>
        </div>

        {/* lg+ */}
        <div className="hidden lg:block">
          <table className="w-full h-px table-fixed">
            <caption className="sr-only">Feature plan comparison</caption>
            <thead>
              <tr>
                <th
                  className="w-full h-full py-8 px-6 text-gray-900 dark:text-white text-left grid grid-cols-1 content-between"
                  scope="row"
                >
                  <div>
                    <Link
                      to="/pricing"
                      className="flex mr-auto items-center cursor-pointer text-lg font-bold"
                    >
                      <ArrowLeftIcon className="w-3 mr-2" />
                      <span>Return to pricing</span>
                    </Link>
                  </div>

                  {!isEmpty(commonFeatures) && renderHideCommonPlansSwitch}
                </th>
                {plans
                  .filter(({ detailed_features }) => !!detailed_features)
                  .map((plan) => (
                    <th key={plan.uuid} className="h-full py-8 px-6 align-top">
                      <div className="flex flex-col items-center justify-center">
                        <span className="font-semibold text-lg capitalize">
                          {plan.name}
                        </span>
                        <div className="flex items-center h-20">
                          <span className="text-2xl mr-1">$</span>
                          <span className="text-5xl font-bold">
                            {(plan.price || 0) / 100}
                          </span>
                          <span className="text-sm font-light dark:text-gray-300 ml-1">
                            /month
                          </span>
                        </div>

                        <button
                          onClick={() => {
                            setNewPlan((prevState) => ({ ...prevState, plan }));
                            history.push("/pricing");
                          }}
                          className="w-full text-xs text-white py-2 mt-4 shadow rounded-md outline-none transition-all bg-hotspotty-400 border-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none"
                        >
                          Select {plan.name}
                        </button>
                      </div>
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody className="border-t border-gray-200 divide-y divide-gray-200 dark:border-gray-500 dark:divide-gray-500">
              {businessPlanDetailedFeatures &&
                keys(businessPlanDetailedFeatures).map(
                  (
                    businessPlanDetailedFeaturesKey,
                    businessPlanDetailedFeaturesKeyIndex
                  ) => (
                    <Fragment key={businessPlanDetailedFeaturesKey}>
                      <tr>
                        <th
                          className="bg-gray-200 dark:bg-gray-700 py-3 pl-6 text-sm font-medium text-gray-900 dark:text-white text-left"
                          colSpan={4}
                          scope="colgroup"
                        >
                          {businessPlanDetailedFeaturesKey}
                        </th>
                      </tr>
                      {keys(
                        businessPlanDetailedFeatures[
                          businessPlanDetailedFeaturesKey
                        ]
                      ).map((featureKey) => {
                        const feature =
                          businessPlanDetailedFeatures[
                            businessPlanDetailedFeaturesKey
                          ][featureKey];

                        if (!feature) return null;

                        if (
                          hideCommonFeatures &&
                          commonFeatures.includes(feature.title)
                        ) {
                          return null;
                        }

                        return (
                          <tr
                            key={`${feature.title}-${businessPlanDetailedFeaturesKeyIndex}`}
                          >
                            <th
                              className="py-5 pl-6 text-sm font-normal text-gray-500 dark:text-gray-300 text-left"
                              scope="row"
                            >
                              {feature.url ? (
                                <ExternalLink
                                  className="break-normal overflow-visible whitespace-normal"
                                  url={feature.url}
                                  text={feature.title}
                                />
                              ) : (
                                feature.title
                              )}
                            </th>
                            {plans.map((item, index) => (
                              <td
                                key={`${item.uuid}-${index}`}
                                className="py-5 px-6"
                              >
                                <div className="flex justify-center">
                                  {item.detailed_features &&
                                  item.detailed_features[
                                    businessPlanDetailedFeaturesKey
                                  ] &&
                                  item.detailed_features[
                                    businessPlanDetailedFeaturesKey
                                  ][featureKey]?.hidden ? (
                                    <MinusIcon
                                      className="h-5 w-5 text-gray-400"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <CheckIcon
                                      className="h-5 w-5 text-green-500"
                                      aria-hidden="true"
                                    />
                                  )}
                                </div>
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </Fragment>
                  )
                )}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 dark:border-gray-500">
                <th className="sr-only" scope="row">
                  Choose your plan
                </th>
                {plans.map((plan) => (
                  <td key={plan.uuid} className="pt-5 px-6">
                    <button
                      onClick={() => {
                        setNewPlan((prevState) => ({ ...prevState, plan }));
                        history.push("/pricing");
                      }}
                      className="w-full text-xs text-white py-2 mt-4 shadow rounded-md outline-none transition-all bg-hotspotty-400 border-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none"
                    >
                      Select {plan.name}
                    </button>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CompareFeaturePlansPage;
